import React, { useState, useEffect, useContext, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CategoryDropdown from '../components/CategoryDropdown';
import MessageDetailsModal from '../components/MessageDetailsModal';
import PreviewModal from '../components/PreviewModal';
import { getMessages } from '../API/message';
import getName from '../util/getName';
import { AppContext } from '../util/AppContext';
import PrintOptionDropdown from '../components/printOptions/printOptionDropdown';
import { documentGenerate, documentGeneratePlanless, documentGenerationType } from '../API/documentGenerate';
import ToastAlert from '../components/ToastAlert';
import { getConfigValue, getConfigCollection } from '../util/getConfigValue';
import Mustache from 'mustache';
import { ProductsModal, ProductsModalButton } from '../components/ProductsModal';
import MessageTypes from '../util/messageTypes';
import { quickSheetTypeMessageId } from '../util/constants';
import { useErrorBoundary } from 'react-error-boundary';
import useScrollPosition from '../hooks/useScrollPosition';
import PlanListTable from '../components/PlanListTable';
import executePrintOptionRestrictions from '../util/printOptionRestrictions';
import { useRoutingValidation } from '../util/useRoutingValidation';
import { FavoriteDesignation, updateHcpAppState } from "../components/FavoriteDesignation";
import { useBreadcrumbs } from '../util/BreadcrumbContext';
import CreateSalesAidStepProgressBar, { SalesAidStepIds } from '../components/CreateSalesAidStepProgressBar';
import { getPrescriberTypeLabel } from '../util/getHcpLabel';

function PlanListPage() {
	const [appState, setAppState] = useContext(AppContext);
	const { state } = useLocation();
	const { showBoundary } = useErrorBoundary();

	const [showProductsModal, setShowProductsModal] = useState(false);
	const [currentProduct, setCurrentProduct] = useState(appState?.currentProduct);
	const [productImageSource, setProductImageSource] = useState(appState?.currentProduct?.imagePath);
	const [prescriberId] = useState(appState.prescriber?.id);
	const [messageCode, setMessageCode] = useState(appState.category?.messageCode);
	const [categories, setCategories] = useState([]);
	const [pageCategory, setPageCategory] = useState(appState.category);
	const [mainMessage, setMainMessage] = useState(appState.mainMessage);
	const [plans, setPlans] = useState(appState.plans);
	const [territoryId] = useState(appState.territory?.id);
	const [marketId] = useState(appState.market?.id);

	const tier = state?.tier || null;
	const planName = state?.planName || null;
	let planId = state?.planId || null;
	const prescriberType = appState?.prescriberType;

	const [isImprovedAccessStatic, setIsImprovedAccessStatic] = useState(false);
	const [currentMessage, setCurrentMessage] = useState(null);
	const [currentPrescriberMessage, setCurrentPrescriberMessage] = useState(null);
	const [minPlans, setMinPlans] = useState(null);
	const [maxPlans, setMaxPlans] = useState(null);

	const [selectedPlans, setSelectedPlans] = useState([]);
	const [preSelectedPlans, setPreSelectedPlans] = useState([]);

	const [showToast, setShowToast] = useState(false);
	const [toastTitle, setToastTitle] = useState('');
	const [toastMessage, setToastMessage] = useState('');
	const [toastAutohide, setToastAutohide] = useState(true);
	const [isLoading, setIsLoading] = useState(false);

	const [showMessageDetailsModal, setShowMessageDetailsModal] = useState(false);
	const [modalGeneratingCode, setModalGeneratingCode] = useState(null);
	const [showPreviewModal, setShowPreviewModal] = useState(false);

	const [isSingleSelection, setIsSingleSelection] = useState(false);
	const [selectionMessage, setSelectionMessage] = useState('Loading...');

	const [isConfigMenuVisible, setIsConfigMenuVisible] = useState(false);
	const hcpNameRequired = getConfigValue(appState.config?.PrintOptions?.HCPName?.Required, appState)?.Value === 'true';
	const ehrBackPageRequired = getConfigValue(appState.config?.PrintOptions?.EhrBackPage?.Required, appState)?.Value === 'true';
	const hcpCoverPageRequired = getConfigValue(appState.config?.PrintOptions?.HCP?.CoverPage?.Required, appState)?.Value === 'true';
	const savingsCardRequired = getConfigValue(appState.config?.PrintOptions?.SavingsCard?.Required, appState)?.Value === 'true';
	const locationNameRequired = getConfigValue(appState.config?.PrintOptions?.IncludeLocationName?.Required, appState)?.Value === 'true';
	const salesRepInfoRequired = getConfigValue(appState.config?.PrintOptions?.SalesRepInfo?.Required, appState)?.Value === 'true';
	const frmInfoRequired = getConfigValue(appState.config?.PrintOptions?.FRMInfo?.Required, appState)?.Value === 'true';
	const [configHCPisChecked, setConfigHCPisChecked] = useState(hcpNameRequired || getConfigValue(appState.config?.PrintOptions?.HCPName?.Default, appState)?.Value === 'true');
	const [configEHRisChecked, setConfigEHRisChecked] = useState(
		ehrBackPageRequired || getConfigValue(appState.config?.PrintOptions?.EhrBackPage?.Default, appState)?.Value === 'true'
	);

	const [selectedColumnState, setSelectedColumnState] = useState([]);
	const [maxDisplayColumns, setMaxDisplayColumns] = useState(5);
	const maxPrintColumns = getConfigValue(appState.config.PlanList.MaxPrintableColumns, appState)?.Value || 5;
	const minPrintColumns = getConfigValue(appState.config.PlanList.MinPrintableColumns, appState)?.Value || 3;

	const [pdfUrl, setPdfUrl] = useState('');
	const [exceedMaxColumns, setExceedMaxColumns] = useState(false);
	const [pdfFileName, setPdfFileName] = useState('');

	const configMenuRef = useRef(null);
	const configMenuContentRef = useRef(null);

	const navigate = useNavigate();

	const scrollPosition = useScrollPosition();
	const planListConfig = useMemo(() => getConfigCollection(appState.config?.PlanList, appState, 'PlanList'), [appState]);
	const printOptionsEhrBackPageEnabled = getConfigValue(appState.config.PrintOptions?.EhrBackPage?.Enabled, appState)?.Value === 'true';
	const printOptionsCoverPageEnabled = getConfigValue(appState.config?.PrintOptions?.HCP?.CoverPage?.Enabled, appState)?.Value === 'true';
	const printOptionsHcpNameEnabled = getConfigValue(appState.config?.PrintOptions?.HCPName?.Enabled, appState)?.Value === 'true';
	const printOptionsLocationNameEnabled = getConfigValue(appState.config?.PrintOptions?.IncludeLocationName?.Enabled, appState)?.Value === 'true';
	const printOptionsSelectSalesRepInfoEnabled = getConfigValue(appState.config.PrintOptions?.SalesRepInfo?.Enabled, appState)?.Value === 'true';
	const printOptionsSelectFRMInfoEnabled = getConfigValue(appState.config.PrintOptions?.FRMInfo?.Enabled, appState)?.Value === 'true';

	const [printOptionsCoverPageIsChecked, setPrintOptionsCoverPageIsChecked] = useState(
		hcpCoverPageRequired || getConfigValue(appState.config?.PrintOptions?.HCP?.CoverPage?.Default, appState)?.Value === 'true'
	);
	const [configLocationNameIsChecked, setConfigLocationNameIsChecked] = useState(
		locationNameRequired || getConfigValue(appState.config.PrintOptions?.IncludeLocationName?.Default, appState)?.Value === 'true'
	);
	const [savingsCardIsChecked, setSavingsCardIsChecked] = useState(
		savingsCardRequired || getConfigValue(appState.config?.PrintOptions?.SavingsCard?.Default, appState)?.Value === 'true'
	);
	const savingsCardIsEnabled = getConfigValue(appState.config?.PrintOptions?.SavingsCard?.Enabled, appState)?.Value === 'true';

	const [printOptionsSalesRepInfoIsChecked, setPrintOptionsSalesRepInfoIsChecked] = useState(
		salesRepInfoRequired || getConfigValue(appState.config?.PrintOptions?.SalesRepInfo?.Default, appState)?.Value === 'true'
	);

	const [printOptionsFRMInfoInfoIsChecked, setPrintOptionsFRMInfoInfoIsChecked] = useState(
		frmInfoRequired || getConfigValue(appState.config?.PrintOptions?.FRMInfo?.Default, appState)?.Value === 'true'
	);

	const isSingleOptionSelectionEnabled =
		getConfigValue(appState.config?.PlanList?.Options?.IncludeInPrint?.SelectSingleOption?.Enabled, appState)?.Value === 'true';
	const isSingleOptionFormularyDrillDownEnabled =
		getConfigValue(appState.config?.PlanList?.Options?.IncludeInPrint?.SelectSingleOption?.FormularyDrillDown?.Selectable, appState)?.Value === 'true';
	const isSingleOptionSavingsCardEnabled =
		getConfigValue(appState.config?.PlanList?.Options?.IncludeInPrint?.SelectSingleOption?.SavingsCard?.Selectable, appState)?.Value === 'true';
	const isSingleOptionRestrictionDetailsEnabled =
		getConfigValue(appState.config?.PlanList?.Options?.IncludeInPrint?.SelectSingleOption?.RestrictionDetails?.Selectable, appState)?.Value === 'true';
	const isSingleOptionSpecialtyPharmacyEnabled =
		getConfigValue(appState.config?.PlanList?.Options?.IncludeInPrint?.SelectSingleOption?.SpecialtyPharmacy?.Selectable, appState)?.Value === 'true';
	const [selectableOptions, setSelectableOptions] = useState({});
	
	const selectAllEnabled = getConfigValue(appState.config?.PlanList?.SelectAll?.Enabled, appState)?.Value === 'true';
	const autoSelectFormularyDrillDownEnabled = getConfigValue(appState.config?.Planlist?.FormularyDrillDown?.AutoSelect?.Enabled, appState)?.Value === 'true';
	const autoSelectRestrictionDetailsEnabled = getConfigValue(appState.config?.Planlist?.RestrictionDetails?.AutoSelect?.Enabled, appState)?.Value === 'true';
	const autoSelectSpecialtyPharmacyEnabled = getConfigValue(appState.config?.Planlist?.SpecialtyPharmacy?.AutoSelect?.Enabled, appState)?.Value === 'true';
	const selectedPlansCountEnabled = getConfigValue(appState.config?.PlanList?.SelectedPlansCount?.Enabled, appState)?.Value === 'true';
	const maxPrintablePlansValue = getConfigValue(appState.config.PlanList.MaxPrintablePlans, appState)?.Value;
	const hasMaxPrintablePlans = maxPrintablePlansValue !== null && maxPrintablePlansValue !== undefined && maxPrintablePlansValue !== '';
	const shouldShowSelectAll = selectAllEnabled;
	const shouldHideMinMaxPrintMessage = selectAllEnabled && !hasMaxPrintablePlans;
	const hasPlanTable = currentPrescriberMessage?.hasPlanTable !== false;
	const hasOptionalPrintOptions =
		(printOptionsHcpNameEnabled && !hcpNameRequired) ||
		(printOptionsEhrBackPageEnabled && !ehrBackPageRequired) ||
		(printOptionsCoverPageEnabled && !hcpCoverPageRequired) ||
		(savingsCardIsEnabled && !savingsCardRequired) ||
		(printOptionsLocationNameEnabled && !locationNameRequired) ||
		(printOptionsSelectSalesRepInfoEnabled && !salesRepInfoRequired) ||
		(printOptionsSelectFRMInfoEnabled && !frmInfoRequired);
	const [selectAllChecked, setSelectAllChecked] = useState(false);

	const parentState = {
		isLoading: isLoading,
		isSingleSelection: isSingleSelection,
		savingsCardIsChecked: savingsCardIsChecked,
		selectionMessage: selectionMessage,
		exceedMaxColumns: exceedMaxColumns,
		maxDisplayColumns: maxDisplayColumns,
		isImprovedAccessStatic: isImprovedAccessStatic,
		selectableOptions: selectableOptions,
		isSingleOptionSelectionEnabled: isSingleOptionSelectionEnabled,
		isSingleOptionFormularyDrillDownEnabled: isSingleOptionFormularyDrillDownEnabled,
		isSingleOptionRestrictionDetailsEnabled: isSingleOptionRestrictionDetailsEnabled,
		isSingleOptionSpecialtyPharmacyEnabled: isSingleOptionSpecialtyPharmacyEnabled,
		isSingleOptionSavingsCardEnabled: isSingleOptionSavingsCardEnabled,
		selectedPlans: selectedPlans,
		preSelectedPlans: preSelectedPlans
	};
	const { setBreadcrumbs } = useBreadcrumbs();

	useEffect(() => {
		setBreadcrumbs([
			{ label: getConfigValue(appState.config?.Focus?.Header, appState)?.Value, path: "/FocusPage" },
		]);
	}, [setBreadcrumbs]);

	useRoutingValidation();

	function isPageValid() {
		if (!hasPlanTable) {
			return true;
		}

		//check min selected columns
		if (selectedColumnState.length < minPrintColumns) {
			return false;
		}

		if (selectedPlans.length < minPlans || (maxPlans !== null && selectedPlans.length > maxPlans)) {
			return false;
		}
		//check soft restrictions for each message plan
		for (let plan of selectedPlans) {
			if (
				plan.minPrintPlans &&
				parseInt(plan.minPrintPlans) > 0 &&
				selectedPlans.filter((e) => e.planClass !== plan.planClass).length < parseInt(plan.minPrintPlans)
			) {
				return false;
			}
		}
		return true;
	}
	const isValid = isPageValid();

	function isPlanSelectableForSelectAll(planRow) {
		const planData = planRow?.data;
		if (!planData) {
			return false;
		}

		const isHardPrintRestricted = planData.isPrintable === false;
		const isSoftPrintRestricted = planData.planClass !== null && planData.planClass !== undefined && planData.planClass !== '';

		return !isHardPrintRestricted && !isSoftPrintRestricted;
	}

	useEffect(() => {
		if (categories === null || categories.length === 0) {
			getMessageCategories();
		}

		updateSelectMessageCategory(pageCategory);

		// set the initial controls
		setSelectableOptions({
			canSelectFormulary: true,
			canSelectRestrictions: true,
			canSelectSpecialtyPharmacy: true,
			canSelectSavingsCard: true,
			canSelectSalesRepNameInfo: true,
			canSelectFRMInfo: true
		});

		executePrintOptionRestrictions({}, parentState, setSelectableOptions, showTooManyOptionsSelectedMessage);
	}, []);

	useEffect(() => {
		if (appState?.config?.PlanList?.MaxDisplayColumns) {
			setMaxDisplayColumns(parseInt(getConfigValue(appState.config.PlanList.MaxDisplayColumns, appState)?.Value));
		}
	}, [appState.category]);

	useEffect(() => {
		if (currentPrescriberMessage) {
			if ((minPlans === 1 && maxPlans === 1) || isSingleSelection) {
				setIsSingleSelection(true);
				setSelectionMessage(getConfigValue(appState.config.PlanList.SingleSelectionMessage, appState)?.Value);
		}
		else if (shouldHideMinMaxPrintMessage) {
				setSelectionMessage('');
			} else {
				let message = getConfigValue(appState.config.PlanList.MinMaxPrintMessage, appState)?.Value;
				setSelectionMessage(Mustache.render(message, planListConfig));
			}
		}
	}, [currentPrescriberMessage, minPlans, maxPlans, isSingleSelection, shouldHideMinMaxPrintMessage, planListConfig]);

	useEffect(() => {
		if (currentMessage && !hasPlanTable && !hasOptionalPrintOptions && !isImprovedAccessStatic) {
			onGenerateClick();
		}
		// eslint-disable-next-line
	}, [currentMessage]);

	useEffect(() => {
		if (messageCode && prescriberId && !pageCategory.isContainerMessage) {
			loadMessages();
		}
		// eslint-disable-next-line
	}, [messageCode, prescriberId, appState?.config, pageCategory]);

	useEffect(() => {
		if (planId && plans && isImprovedAccessStatic && selectedPlans.length === 0) {
			const currentPlan = plans.rows.find((p) => p.data.planId === planId);
			if (currentPlan) {
				const preSelected = [
					{
						...currentPlan.data,
						includeFormularies: autoSelectFormularyDrillDownEnabled && currentPlan.data.formularyDrillDown?.length > 0,
						includeRestrictions: autoSelectRestrictionDetailsEnabled && currentPlan.data.formularyRestriction?.length > 0,
						includeSpecialtyPharmacy: autoSelectSpecialtyPharmacyEnabled && currentPlan.data.specialtyPharmacies?.length > 0,
					},
				];
				setSelectedPlans(preSelected);
				setPreSelectedPlans(preSelected);
			}
		}
	}, [planId, plans, isImprovedAccessStatic, selectedPlans]);

	useEffect(() => {
		if (plans?.rows && shouldShowSelectAll) {
			const selectablePlans = plans.rows.filter(isPlanSelectableForSelectAll);
			const parsedMaxPrintablePlans = Number.parseInt(getConfigValue(appState.config.PlanList.MaxPrintablePlans, appState)?.Value);
			const expectedMaxSelection = Number.isNaN(parsedMaxPrintablePlans)
				? selectablePlans.length
				: Math.min(parsedMaxPrintablePlans, selectablePlans.length);

			const allSelectablePlansSelected = selectablePlans.length > 0 &&
				selectedPlans.length === expectedMaxSelection &&
				selectablePlans.slice(0, expectedMaxSelection).every(p => selectedPlans.some(sp => sp.planId === p.data.planId));
			setSelectAllChecked(allSelectablePlansSelected);
		} else if (!shouldShowSelectAll) {
			setSelectAllChecked(false);
		}
	}, [selectedPlans, plans, shouldShowSelectAll, appState.config]);

	function planListHeaderSticky() {
		const planListHeader = document.getElementById('plan-list-header');
		if (planListHeader) {
			if (scrollPosition > 150) {
				planListHeader.classList.add('plan-list-header-sticky');
				planListHeader.classList.add('border-bottom');
				document.getElementById('plan-list-header-spacer').classList.add('d-block');
				document.getElementById('plan-list-header-spacer').classList.remove('d-none');
			} else if (planListHeader.classList.contains('plan-list-header-sticky')) {
				planListHeader.classList.remove('plan-list-header-sticky');
				planListHeader.classList.remove('border-bottom');
				document.getElementById('plan-list-header-spacer').classList.remove('d-block');
				document.getElementById('plan-list-header-spacer').classList.add('d-none');
			}
		}
	}

	function updateSelectMessageCategory(pageCategory) {
		setMessageCode(pageCategory.messageCode);
		setAppState({
			...appState,
			category: pageCategory,
			messageCode: pageCategory.messageCode,
			isQuickSheetMessageType: pageCategory.messageTypeId === quickSheetTypeMessageId,
		});

		const isImprovedAccess = pageCategory.messageType === MessageTypes.ImprovedAccess || false;
		setIsImprovedAccessStatic(isImprovedAccess);
		const isSingleWinPlan = pageCategory.messageType === MessageTypes.SingleWinPlan || false;
		setIsSingleSelection(isSingleWinPlan);
	}

	function triggerToastAlert(title, message, autohide) {
		setToastTitle(title);
		setToastMessage(message);
		setShowToast(true);
		setToastAutohide(autohide);
	}

	function loadMessages() {
		getMessages(messageCode, prescriberId, appState.currentProduct.id, appState.category.messageTypeId, false, prescriberType)
			.then((responseJson) => {
				const prescriberMessage = responseJson.prescriberMessage;
				const responsePlans = responseJson.dynamicTable || [];
				const responseMainMessage = responseJson.prescriberMessage.mainMessage || '';
				const minPlans = getConfigValue(appState.config.PlanList.MinPrintablePlans, appState)?.Value || null;
			const maxPrintPlans = getConfigValue(appState.config.PlanList.MaxPrintablePlans, appState)?.Value || null;
				if (planId && prescriberMessage.category === 'Improved Access') {
					const selectedPlans = [responsePlans.rows.find((r) => r.data.planId === planId)];
					selectedPlans.push(...responsePlans.rows.filter((r) => r.data.planId !== planId));
					responsePlans.rows = selectedPlans;
				}
				setAppState({
					...appState,
					plans: responsePlans,
					mainMessage: responseMainMessage,
				});
				setCurrentMessage(responseJson);
				setCurrentPrescriberMessage(prescriberMessage);
				setPlans(responsePlans);
				setMainMessage(responseMainMessage);
				setMinPlans(minPlans);
				setMaxPlans(maxPrintPlans);
				if (responsePlans?.headers) {
					//pre select the columns to the max number of visible columns defined					
					const visibleColumns = [];
					for (const header of responsePlans.headers) {
						if ((header.isRequired || header.autoSelect) && visibleColumns.length < maxPrintColumns) {
							visibleColumns.push(header.key);
						}
					}
					setSelectedColumnState(visibleColumns);
					setExceedMaxColumns(responsePlans?.headers.length > maxDisplayColumns);

				}
			})
			.catch((e) => showBoundary(e));
	}

	function getMessageCategories() {
		if (appState.products.length > 0) {
			const categories = appState.products.find((x) => {
				return x.productId === appState.currentProduct.clientProductId;
			});
			setCategories(categories.messages);
		}
	}

	function getImprovedMessage(mainMessage) {
		if (tier && planName) {
			return Mustache.render(mainMessage, { Tier: tier, PlanName: planName });
		} else {
			return mainMessage;
		}
	}

	function onProductClick(product) {
		if (product.id === appState.currentProduct.id) {
			setShowProductsModal(false);
			return;
		}
		setAppState({ ...appState, currentProduct: product });
		setCurrentProduct(product);
		setShowProductsModal(false);
		setProductImageSource(product.imagePath);
		navigate('/MessageSelectionPage');
	}

	function showTooManyOptionsSelectedMessage() {
		let availableOptions = [];

		if (isSingleOptionFormularyDrillDownEnabled) {
			availableOptions.push(getConfigValue(appState.config?.PlanList?.Options?.Formularies, appState)?.Value);
		}

		if (isSingleOptionRestrictionDetailsEnabled) {
			availableOptions.push(getConfigValue(appState.config?.PlanList?.Options?.Restrictions, appState)?.Value);
		}

		if (isSingleOptionSavingsCardEnabled) {
			availableOptions.push(getConfigValue(appState.config?.PlanList?.ConfigureOption?.SavingsCard, appState)?.Value);
		}

		if (isSingleOptionSpecialtyPharmacyEnabled) {
			availableOptions.push(getConfigValue(appState.config?.PlanList?.Options?.SpecialtyPharmacy, appState)?.Value);
		}

		const errorMessage = getConfigValue(
			appState.config?.PlanList?.Options?.IncludeInPrint?.SelectSingleOption?.TooManyItemsSelected?.Error,
			appState
		)?.Value;
		const selectableOptions = availableOptions.join(' or ').toLowerCase();

		triggerToastAlert(
			getConfigValue(appState.config?.PlanList?.Options?.IncludeInPrint?.SelectSingleOption?.TooManyItemsSelected?.Title, appState)?.Value,
			Mustache.render(errorMessage, { selectableOptions }),
			false
		);
	}

	function closeOpenMenus(event) {
		// click outside of menus and content menus = close menus
		if (
			configMenuRef.current &&
			!configMenuRef.current.contains(event.target) &&
			!configMenuContentRef.current.contains(event.target) &&
			isConfigMenuVisible
		) {
			setIsConfigMenuVisible(false);
		}
	}

	function onDetailsClick() {
		setShowMessageDetailsModal(true);
	}

	function handleBackButton() {
		if (appState.category.messageType === MessageTypes.ImprovedAccess) {
			navigate('/WinPlanListPage');
		} else {
			navigate('/MessageSelectionPage');
		}
	}

	function navigateToPage(category) {
		const isContainerMessage = category.isContainerMessage;
		if (category.messageType === MessageTypes.ImprovedAccess) {
			navigate('/WinPlanListPage');
		} else if (isContainerMessage) {
			navigate('/ContainerMessagePage');
		} else if (category.messageTypeId === quickSheetTypeMessageId) {
			navigate('/QuickSheetsPage');
		} else {
			navigate('/PlanListPage');
		}
	}

	async function onDetailsCategoryClick(category) {
		const wouldGoToPlanList = !category.isContainerMessage && category.messageType !== MessageTypes.ImprovedAccess;
		if (wouldGoToPlanList && !hasOptionalPrintOptions && category.hasPlanTable === false) {
			try {
				setModalGeneratingCode(category.messageCode);
				const props = {
					productId: currentProduct.clientProductId,
					prescriberId: prescriberId,
					messageType: category.messageCode,
					selectedPlans: [],
					printPrescriberName: printOptionsHcpNameEnabled && configHCPisChecked,
					addEHRBackPage: configEHRisChecked,
					marketid: Number(marketId),
					territoryid: territoryId,
					generateFor: documentGenerationType.Generate,
					columnKeys: selectedColumnState,
					messageTypeId: category.messageTypeId,
					improvedAccessPlan: null,
					includeLocationName: printOptionsLocationNameEnabled && configLocationNameIsChecked,
					includeSavingsCard: savingsCardIsChecked,
					addCoverPage: printOptionsCoverPageIsChecked,
					targetLevel: prescriberType,
					includeSalesRep: printOptionsSalesRepInfoIsChecked,
					includeFRM: printOptionsFRMInfoInfoIsChecked,
					timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
					purchaseOrderNumber: appState.purchaseOrderNumber
				};
				const response = await documentGeneratePlanless(props);
				setAppState({
					...appState,
					lastGeneratedDocument: props,
					sellSheetId: response.sellSheetId,
					purchaseOrderNumber: response.purchaseOrderNumber,
				});
				const url = URL.createObjectURL(response.blob);
				navigate('/GenerateDocumentPage', { state: { pdfURL: url, fileName: response.fileName } });
				return;
			} catch (err) {
				console.error(err);
				triggerToastAlert('Error', getConfigValue(appState.config?.GenerateDocument?.Error?.Text, appState)?.Value, true);
				return;
			} finally {
				setModalGeneratingCode(null);
			}
		}
		setPageCategory(category);
		setShowMessageDetailsModal(false);
		updateSelectMessageCategory(category);
		navigateToPage(category);
		setSelectedPlans([]);
		planId = null;
	}

	document.addEventListener('mousedown', closeOpenMenus);

	async function onPreviewClick() {
		setPdfUrl(null);
		setShowPreviewModal(true);
		const response = await loadDoc(documentGenerationType.Preview);
		const url = URL.createObjectURL(response.blob);
		setPdfFileName(response.fileName);
		setPdfUrl(url);
	}

	async function loadDoc(generationType) {
		setIsLoading(true);

		const props = {
			productId: currentProduct.clientProductId,
			prescriberId: prescriberId,
			messageType: pageCategory.messageCode,
			selectedPlans: selectedPlans.map((plan) => ({
				planId: plan.planId,
				includeRestrictions: plan.includeRestrictions,
				includeFormularies: plan.includeFormularies,
				includeSpecialtyPharmacy: plan.includeSpecialtyPharmacy,
				competitiveProductPrintOptions: plan.includeCompetitiveRestrictionNotes ?? [],
			})),
			printPrescriberName: printOptionsHcpNameEnabled && configHCPisChecked,
			addEHRBackPage: configEHRisChecked,
			marketid: Number(marketId),
			territoryid: territoryId,
			generateFor: generationType,
			columnKeys: selectedColumnState,
			messageTypeId: pageCategory.messageTypeId,
			improvedAccessPlan: planId,
			includeLocationName: printOptionsLocationNameEnabled && configLocationNameIsChecked,
			includeSavingsCard: savingsCardIsChecked,
			addCoverPage: printOptionsCoverPageIsChecked,
			targetLevel: prescriberType,
			includeSalesRep: printOptionsSalesRepInfoIsChecked,
			includeFRM: printOptionsFRMInfoInfoIsChecked,
			timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
			purchaseOrderNumber: appState.purchaseOrderNumber
		};
		const response = await (hasPlanTable ? documentGenerate(props) : documentGeneratePlanless(props))
			.then((responseJson) => {
				setIsLoading(false);

				// store the properties of the last document generated
				setAppState({
					...appState,
					lastGeneratedDocument: props,
					sellSheetId: responseJson.sellSheetId,
					purchaseOrderNumber: responseJson.purchaseOrderNumber,
				});

				return responseJson;
			})
			.catch((err) => {
				setShowPreviewModal(false);
				setIsLoading(false);
				triggerToastAlert('Error', getConfigValue(appState.config?.GenerateDocument?.Error?.Text, appState)?.Value, true);
				console.error(err);
			});
		return response;
	}

	async function onGenerateClick() {
		const response = await loadDoc(documentGenerationType.Generate);
		const url = URL.createObjectURL(response.blob);
		navigate('/GenerateDocumentPage', {
			state: { pdfURL: url, fileName: response.fileName },
		});
	}

	function updateSelectedPlans(_, selectedPlans) {
		setSelectedPlans(selectedPlans);
	}

	function getSelectedPlansCountMessage() {
		const selectedPlansCountMessage = getConfigValue(appState.config?.PlanList?.SelectedPlansCount?.Message, appState)?.Value;
		const availablePlansCount = plans?.rows?.length;
		if (selectedPlansCountEnabled && selectedPlansCountMessage && Number.isInteger(availablePlansCount)) {
			return Mustache.render(selectedPlansCountMessage, {
				currentSelectedPlans: selectedPlans.length,
				availablePlans: availablePlansCount
			});
		}
		return '';
	}

	function handleSelectAllClick(checked) {
		if (!shouldShowSelectAll) {
			return;
		}
		setSelectAllChecked(checked);
		if (checked && plans?.rows) {
			const selectablePlans = plans.rows
				.filter(isPlanSelectableForSelectAll)
				.map(row => ({
					...row.data,
					includeFormularies: autoSelectFormularyDrillDownEnabled && row.data.formularyDrillDown?.length > 0,
					includeRestrictions: autoSelectRestrictionDetailsEnabled && row.data.formularyRestriction?.length > 0,
					includeSpecialtyPharmacy: autoSelectSpecialtyPharmacyEnabled && row.data.specialtyPharmacies?.length > 0,
				}));

			const parsedMaxPrintablePlans = Number.parseInt(getConfigValue(appState.config.PlanList.MaxPrintablePlans, appState)?.Value);
			const plansToSelect = Number.isNaN(parsedMaxPrintablePlans)
				? selectablePlans
				: selectablePlans.slice(0, parsedMaxPrintablePlans);
			setSelectedPlans(plansToSelect);
		} else {
			setSelectedPlans([]);
		}
	}

	return (
		<>
			<div className='page-content-with-sticky-actions' style={hasPlanTable ? { minHeight: 'calc(100vh - 150px)' } : {}}>
				<CreateSalesAidStepProgressBar currentStepId={SalesAidStepIds.CONFIGUREMESSAGE} focusType={appState?.focus} />

				<div className='container-fluid pt-3 pt-md-4 pb-1 color-bg-bodybg' id='plan-list-header' onScroll={planListHeaderSticky()}>
					<div className='container-xl'>
						<div className='row d-flex align-items-md-center mb-2 mb-lg-4'>
							<div className='col-12 col-md-6 order-2 order-md-1'>
								<h1 className='type-20 color-grey-d1 mt-4 mt-md-0 hide-sticky'>
									{getConfigValue(appState.config?.PlanList?.Title, appState)?.Value}
								</h1>
								<div className='row justify-content-end show-sticky-only'>
									<div className='row justify-content-start'>
										<div className='col-6 col-md-auto type-14 text-md-start'>
											<div className='color-grey-d1'>{getConfigValue(appState.config?.PlanList?.ProductLabel, appState)?.Value}</div>
											{appState.currentProduct.name}
										</div>
										<div className='col-6 col-md-auto type-14 text-md-start'>
											<div className='color-grey-d1'>{getConfigValue(appState.config?.PlanList?.MessageTypeLabel, appState)?.Value}</div>
											{pageCategory.category}
										</div>
									</div>
								</div>
							</div>
							<div className='col-12 col-md-6 order-1 order-md-2'>
								<div className='row justify-content-end'>
									<div className='col-6 col-md-auto type-14 text-md-end favorite-hcp'>
										<div className='color-grey-d1'>
											{getPrescriberTypeLabel("PlanList", prescriberType, appState)}
										</div>
										<FavoriteDesignation hcp={appState.prescriber} updateCallback={() => updateHcpAppState(appState.prescriber, setAppState)} ></FavoriteDesignation>{getName(appState)}
									</div>
									<div className='col-6 col-md-auto type-14 text-md-end'>
										<div className='color-grey-d1'>{getConfigValue(appState.config?.PlanList?.TerritoryLabel, appState)?.Value}</div>
										{appState.territory?.name}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
				<div className='my-8 d-none' id='plan-list-header-spacer'></div>

				{/* <!-- Dropdowns row --> */}

				<div className='container-xl'>
					<div className='row d-flex mb-5'>
						{/* This section will be replaced with the config data to pull in the brands */}
						<div className='col-12 col-md-4'>
							<ProductsModalButton
								setShowProductsModal={setShowProductsModal}
								productImageSource={productImageSource}
								productName={currentProduct?.productName}
							></ProductsModalButton>
						</div>

						<div className='col-12 col-md-4 mt-4 mt-md-0'>
							<CategoryDropdown pageCategory={pageCategory} onDetailsClick={onDetailsClick} isLoading={isLoading} />
						</div>

						<div className='col-12 col-md-4 mt-4 mt-md-0'>
							<PrintOptionDropdown
								isConfigMenuVisible={isConfigMenuVisible}
								setIsConfigMenuVisible={setIsConfigMenuVisible}
								configMenuContentRef={configMenuContentRef}
								configMenuRef={configMenuRef}
								configHCPisChecked={configHCPisChecked}
								setConfigHCPisChecked={setConfigHCPisChecked}
								configEHRisChecked={configEHRisChecked}
								setConfigEHRisChecked={setConfigEHRisChecked}
								isLoading={isLoading}
								selectedColumnState={selectedColumnState}
								setSelectedColumnState={setSelectedColumnState}
								printConfigColumnHeaders={hasPlanTable ? plans.headers : null}
								isDisabled={false}
								printOptionsEhrBackPageEnabled={printOptionsEhrBackPageEnabled}
								printOptionsCoverPageEnabled={printOptionsCoverPageEnabled}
								printOptionsHcpNameEnabled={printOptionsHcpNameEnabled}
								printOptionsLocationNameEnabled={printOptionsLocationNameEnabled}
								configCoverisChecked={printOptionsCoverPageIsChecked}
								setConfigCoverisChecked={setPrintOptionsCoverPageIsChecked}
								setConfigLocationNameIsChecked={setConfigLocationNameIsChecked}
								configLocationNameIsChecked={configLocationNameIsChecked}
								savingsCardIsChecked={savingsCardIsChecked}
								setSavingsCardIschecked={setSavingsCardIsChecked}
								savingsCardIsEnabled={savingsCardIsEnabled}
								canSelectSavingsCard={selectableOptions.canSelectSavingsCard}
								canSelectSalesRepNameInfo={printOptionsSelectSalesRepInfoEnabled}
								canSelectFRMInfo={printOptionsSelectFRMInfoEnabled}
								printSalesRepoInfoIsChecked={printOptionsSalesRepInfoIsChecked}
								printFRMInfoIsChecked={printOptionsFRMInfoInfoIsChecked}
								setPrintSalesRepoInfoIsChecked={setPrintOptionsSalesRepInfoIsChecked}
								setPrintFRMInfoIsChecked={setPrintOptionsFRMInfoInfoIsChecked}
								hcpNameRequired={hcpNameRequired}
								ehrBackPageRequired={ehrBackPageRequired}
								coverPageRequired={hcpCoverPageRequired}
								savingsCardRequired={savingsCardRequired}
								locationNameRequired={locationNameRequired}
								salesRepInfoRequired={salesRepInfoRequired}
								frmInfoRequired={frmInfoRequired}
								onOptionsClick={(data) => { return executePrintOptionRestrictions(data, parentState, setSelectableOptions, showTooManyOptionsSelectedMessage) }}
							/>
						</div>
					</div>

					{/* <!-- Heading row --> */}
					<div className='row my-5 mb-lg-3'>
						<div className='col-12'>
							<h1
								className='type-24 type-bold text-primary ag_message_title py-3'
								dangerouslySetInnerHTML={{
									__html: getImprovedMessage(mainMessage),
								}}
							></h1>
						</div>
					</div>
					{currentMessage && hasPlanTable && (
						<PlanListTable
							updateSelectedPlans={updateSelectedPlans}
							message={currentMessage}
							parentState={parentState}
							setSelectableOptions={setSelectableOptions}
							triggerToastAlert={triggerToastAlert}
							executePrintOptionRestrictions={executePrintOptionRestrictions}
						showTooManyOptionsSelectedMessage={showTooManyOptionsSelectedMessage}
							selectAllEnabled={shouldShowSelectAll}
						selectAllChecked={selectAllChecked}
						onSelectAllClick={handleSelectAllClick}
					></PlanListTable>
					)}
				</div>
			</div>

			<div className='button-bottom-container-sticky-low-z'>
				<div className='container-xl'>
					<div className='row py-3'>
						<div className='col-3'>
							<div className='d-flex flex-column flex-lg-row'>
								<button
									onClick={onPreviewClick}
									disabled={!isValid || isLoading}
									className={`btn btn-outline-primary btn-lg flex-fill w-100 ${isValid || isLoading ? '' : 'disabled'}`}
								>
									{getConfigValue(appState.config?.PlanList?.Preview.Label, appState)?.Value || null}
								</button>
							</div>
						</div>
						<div className='col-9'>
							<div className='d-flex flex-md-row-reverse'>
								<button
									className={`btn btn-success btn-lg mb-lg-0 ms-lg-3 flex-fill w-100 ml-1 ${isValid || isLoading ? '' : 'disabled'} ${isLoading ? 'btn-working' : ''
										}`}
									type='button'
									disabled={!isValid || isLoading}
									onClick={onGenerateClick}
								>
									{' '}
									{getConfigValue(appState.config?.PlanList?.Generate?.Label, appState)?.Value || null}
								</button>
								<button type='button' onClick={handleBackButton} className='btn btn-secondary btn-lg flex-fill w-100'>
									{getConfigValue(appState.config?.Common?.Back, appState)?.Value || null}
								</button>
								<div className='flex-fill w-100 ms-lg-3 pe-3 d-flex align-items-center justify-content-center'>
									<div className='text-center type-14 color-grey-d1'>
										{hasPlanTable && getSelectedPlansCountMessage()}
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<ToastAlert show={showToast} setShow={setShowToast} title={toastTitle} message={toastMessage} autohide={toastAutohide} />

			<MessageDetailsModal
				show={showMessageDetailsModal}
				setShow={setShowMessageDetailsModal}
				categories={categories}
				onDetailsCategoryClick={onDetailsCategoryClick}
				loadingCategoryCode={modalGeneratingCode}
			/>

			<PreviewModal
				show={showPreviewModal}
				setShow={setShowPreviewModal}
				onGenerateClick={onGenerateClick}
				isValid={isValid}
				isLoading={isLoading}
				pdfUrl={pdfUrl}
				showGenerateBack={true}
				showPrintDownload={false}
				pdfFileName={pdfFileName}
				supportsProfessionalPrinting={true}
				skipPreviewTrack={true}
				returnURL={'/planListPage'}
			/>

			<ProductsModal show={showProductsModal} setShow={setShowProductsModal} onProductClick={onProductClick} onlySellSheetProducts={true} />
		</>
	);
}

export default PlanListPage;
